import React, { useState, useContext, useEffect } from "react";
import { parse, format, isBefore, isAfter } from "date-fns";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faMapMarkerAlt, faTicketAlt } from '@fortawesome/free-solid-svg-icons';
import './css/calendar.css';
import { DataContext } from "./sub-components/DataContext";
import EventSchema from "./sub-components/EventSchema";
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ReactGA from 'react-ga4';

const Calendar = () => {
    const { events, fetchEvents, showPastEvents, setShowPastEvents } = useContext(DataContext);
    const { pathname } = useLocation();
    const [isLoading, setIsLoading] = useState(false);

    // Month filter state: 'all' means no filter
    const [selectedMonth, setSelectedMonth] = useState('all');

    // Fetch events on mount
    useEffect(() => {
        let didCancel = false;
        let hasFetched = false;

        const loadEvents = async () => {
            setIsLoading(true);
            try {
                await fetchEvents();
            } catch (err) {
                console.error("Error fetching events", err);
            } finally {
                if (!didCancel) setIsLoading(false);
            }
        };

        if (!hasFetched && (!events || events.length === 0)) {
            hasFetched = true;
            loadEvents();
        }

        return () => { didCancel = true; };
    }, [events, fetchEvents]);

    // Parse event dates safely
    const parseEventDate = (dateStr) => {
        try {
            // Handles dates like "10/17/2025 2:00:00 PM -07:00"
            return parse(dateStr, "MM/dd/yyyy h:mm:ss a XXX", new Date());
        } catch {
            return null;
        }
    };

    // Base filter: upcoming vs past, plus parsedDate
    const filteredEvents = (events || [])
        .map(event => ({ ...event, parsedDate: parseEventDate(event.start_time) }))
        .filter(event => event.parsedDate)
        .filter(event =>
            showPastEvents
                ? isBefore(event.parsedDate, new Date())
                : isAfter(event.parsedDate, new Date())
        );

    // Build month options dynamically from available events
    const uniqueMonthKeys = Array.from(
        new Set(
            filteredEvents.map(e => format(e.parsedDate, 'yyyy-MM')) // e.g. "2025-10"
        )
    ).sort();

    const monthOptions = [
        { value: 'all', label: 'All months' },
        ...uniqueMonthKeys.map(key => ({
            value: key,
            label: format(parse(key, 'yyyy-MM', new Date()), 'MMMM yyyy'),
        })),
    ];

    // Apply month filter
    const monthFilteredEvents = filteredEvents.filter(event => {
        if (selectedMonth === 'all') return true;

        const eventMonthKey = format(event.parsedDate, 'yyyy-MM');
        return eventMonthKey === selectedMonth;
    });

    // Sort events by parsedDate ascending
    const sortedEvents = monthFilteredEvents.sort(
        (a, b) => a.parsedDate - b.parsedDate
    );

    // Google Calendar link
    const formatGoogleCalendarLink = (event) => {
        const startTime = new Date(event.start_time)
            .toISOString()
            .replace(/-|:|\.\d+/g, "");
        const endTime = new Date(event.end_time || event.start_time)
            .toISOString()
            .replace(/-|:|\.\d+/g, "");
        const eventTitle = encodeURIComponent(event.name);
        const eventDetails = encodeURIComponent(event.description || "");
        const eventLocation = encodeURIComponent(event.place?.name || "Online");

        return `https://calendar.google.com/calendar/r/eventedit?text=${eventTitle}&dates=${startTime}/${endTime}&details=${eventDetails}&location=${eventLocation}`;
    };

    // Handle sharing events
    const handleShare = async (event) => {
        const eventUrl = `https://www.dysband.com/EventPage/${event.id}`;
        const eventDate = event.parsedDate.toLocaleString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });

        const eventText = `${event.name}\n${eventDate}\n${event.description || ''}\n${eventUrl}`;
        const shareData = {
            title: `Dirty Yachtin' Scoundrels Yacht Rock Event: ${event.name}`,
            text: `${event.name} on ${eventDate}\n${event.description || ''}\n`,
            url: `${eventUrl}`
        };

        // GA4
        ReactGA.event({
            category: 'Calendar',
            action: 'Click Share Event',
            label: `${event.name} on ${eventDate}`,
            value: 1,
        });

        // GTM (plain JS, no TypeScript assertion)
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: 'calendar_share_click',
            category: 'Calendar',
            action: 'Click Share Event',
            label: `${event.name} on ${eventDate}`,
            value: 1
        });

        if (navigator.share) {
            try {
                await navigator.share(shareData);
            } catch (err) {
                console.error('❌ Error sharing:', err);
            }
        } else {
            try {
                await navigator.clipboard.writeText(eventText);
                alert('Event details copied to clipboard!');
            } catch (err) {
                console.error('❌ Copy to clipboard failed:', err);
            }
        }
    };

    return (
        <div style={{ fontFamily: "Arial, sans-serif", padding: "20px" }}>
            <Helmet>
                <title>
                    Dirty Yachtin' Scoundrels Calendar | Upcoming Yacht Rock Concerts & Events
                </title>
                <meta
                    name="description"
                    content="Catch the yacht rock band Dirty Yachtin' Scoundrels live in Phoenix, Scottsdale, and beyond. Discover upcoming concerts, festivals, private events, and public shows."
                />
                <meta property="og:url" content={`https://www.dysband.com${pathname}`} />
            </Helmet>

            <EventSchema />

            {/*<h1>{showPastEvents ? "Past Events" : "Upcoming Eventss"}</h1>*/}
            <h1>
                {
                    selectedMonth === 'all' ? 'Upcoming Events' : "Upcoming Events " + (monthOptions.find(m => m.value === selectedMonth)?.label || 'Upcoming Events')
                }
            </h1>

            {/* Month filter dropdown */}
            <div className="month-filter">
                <label htmlFor="monthFilter" className="month-filter__label">Filter by month: </label>
                <select id="monthFilter" className="month-filter__select" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
                    {monthOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                            {opt.label}
                        </option>
                    ))}
                </select>
            </div>

            {isLoading ? (
                <div className="spinner-container">
                    <p>Loading Calendar</p>
                    <div className="spinner"></div>
                </div>
            ) : sortedEvents.length === 0 ? (
                <p>No events to show.</p>
            ) : (
                <div className="calendar-cont">
                    {sortedEvents.map((event, index) => {
                        const formattedDate = format(event.parsedDate, "MMMM d, yyyy");
                        const formattedTime = format(event.parsedDate, "h:mm a");
                        const monthName = format(event.parsedDate, "MMM");
                        const day = format(event.parsedDate, "d");

                        return (
                            <div
                                key={index}
                                className="calendar-item"
                                style={{
                                    border:
                                        event.name === "Private Event"
                                            ? "1px solid grey"
                                            : "1px solid #ddd",
                                    padding: "10px",
                                    borderRadius: "8px",
                                    background:
                                        event.name === "Private Event"
                                            ? "#ababab"
                                            : "rgba(255,255,255,1.00)"
                                }}
                            >
                                <div className="sk-event-item-cont">
                                    <div className="sk-event-item-thumbnail-date">
                                        <div className="sk-event-item-thumbnail-month">
                                            <p>{monthName}</p>
                                        </div>
                                        <div className="sk-event-item-thumbnail-day">
                                            <p>{day}</p>
                                        </div>
                                    </div>

                                    <div>
                                        <h3 style={{ marginTop: "7px" }}>{event.name}</h3>
                                        <p>
                                            <FontAwesomeIcon
                                                icon={faCalendarAlt}
                                                size="1x"
                                                color="grey"
                                            />{" "}
                                            {formattedDate} at {formattedTime}
                                        </p>
                                        {event.place?.name &&
                                            event.name !== "Private Event" &&
                                            !showPastEvents && (
                                                <p>
                                                    <FontAwesomeIcon
                                                        icon={faMapMarkerAlt}
                                                        size="1x"
                                                        color="grey"
                                                    />{" "}
                                                    <a
                                                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                                                            event.place.name
                                                        )}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {event.place.name}
                                                    </a>
                                                </p>
                                            )}
                                        {event.ticket_uri && (
                                            <p>
                                                <FontAwesomeIcon
                                                    icon={faTicketAlt}
                                                    size="1x"
                                                    color="grey"
                                                />{" "}
                                                <a
                                                    href={event.ticket_uri}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Get Tickets
                                                </a>
                                            </p>
                                        )}
                                        <p>
                                            <a
                                                href={`https://facebook.com/events/${event.id}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                View Event on Facebook
                                            </a>
                                        </p>
                                        <div style={{ marginBottom: '1em' }}>
                                            <div
                                                dangerouslySetInnerHTML={{
                                                    __html: event.description,
                                                }}
                                            />
                                        </div>

                                        {event.name !== "Private Event" && !showPastEvents && (
                                            <>
                                                <a
                                                    href={formatGoogleCalendarLink(event)}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    <button style={{ marginBottom: "1rem" }}>
                                                        Add to Google Calendar
                                                    </button>
                                                </a>
                                                <button
                                                    onClick={() => handleShare(event)}
                                                    className="share-event"
                                                >
                                                    Share Event
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default Calendar;